import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Newspaper, ExternalLink, Calendar, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  sante: 'Santé',
  environnement: 'Environnement',
  developpement: 'Développement',
  agriculture: 'Agriculture',
  societe: 'Société',
  autre: 'Autre',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
};

export default function NewsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchNews = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/news`);
      setItems(res.data || []);
    } catch (e) {
      console.error('Failed to load news:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNews(); }, [fetchNews]);

  const categories = ['all', ...Array.from(new Set(items.map((i) => i.category).filter(Boolean)))];
  const filtered = filter === 'all' ? items : items.filter((i) => i.category === filter);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <section className="section-padding bg-white" data-testid="news-page">
        <div className="container mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#D87D4A]/10 mb-4">
              <Newspaper className="text-[#D87D4A]" size={40} />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#2E332D] mb-3"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Actualités
            </h1>
            <p className="text-base text-[#5C665A] max-w-2xl mx-auto">
              Suivez l&apos;actualité du secteur — santé, environnement, agriculture, société et
              développement communautaire en RD Congo.
            </p>
          </div>

          {categories.length > 1 && (
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {categories.map((c) => (
                <button key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`news-cat-${c}`}
                  className={`px-4 py-2 rounded-full text-sm font-bold border-2 transition-all ${
                    filter === c
                      ? 'bg-[#4A6B48] border-[#4A6B48] text-white'
                      : 'border-[#E5E3DB] text-[#5C665A] hover:border-[#4A6B48] hover:text-[#4A6B48]'
                  }`}>
                  {c === 'all' ? 'Toutes' : (CATEGORY_LABELS[c] || c)}
                </button>
              ))}
            </div>
          )}

          {loading && (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin text-[#4A6B48]" size={32} />
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <p className="text-center text-[#5C665A] py-12" data-testid="news-empty">
              Aucune actualité disponible pour cette catégorie.
            </p>
          )}

          {!loading && filtered.length > 0 && (
            <div className="grid gap-4 max-w-4xl mx-auto">
              {filtered.map((n) => (
                <article key={n.news_id || n.id}
                  className="card hover:-translate-y-0.5 transition-transform"
                  data-testid={`news-item-${n.news_id || n.id}`}>
                  <div className="flex items-center gap-2 mb-2 flex-wrap text-xs">
                    {n.category && (
                      <span className="font-bold px-2 py-0.5 bg-[#4A6B48]/10 text-[#4A6B48] rounded uppercase">
                        {CATEGORY_LABELS[n.category] || n.category}
                      </span>
                    )}
                    {n.source && <span className="text-[#5C665A] italic">— {n.source}</span>}
                    {n.published_at && (
                      <span className="text-[#5C665A] inline-flex items-center gap-1 ml-auto">
                        <Calendar size={12} /> {formatDate(n.published_at)}
                      </span>
                    )}
                  </div>
                  <h2 className="text-xl font-bold text-[#2E332D] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {n.title}
                  </h2>
                  {n.content && <p className="text-sm text-[#5C665A] leading-relaxed line-clamp-3">{n.content}</p>}
                  {n.url && (
                    <a href={n.url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-bold text-[#4A6B48] hover:underline mt-3">
                      Lire la source <ExternalLink size={14} />
                    </a>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
